import React, { useState, useEffect } from 'react';
import { X, Shield, QrCode, Copy, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../config/axios';
import LoadingSpinner from '../UI/LoadingSpinner';

const TwoFactorSetup = ({ isOpen, onClose, onSetupComplete }) => {
    const [step, setStep] = useState(1); // 1: QR Code, 2: Verify
    const [qrCode, setQrCode] = useState('');
    const [secret, setSecret] = useState('');
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setupTwoFactor();
        }
    }, [isOpen]);

    const setupTwoFactor = async () => {
        setLoading(true);
        try {
            const response = await api.post('/api/auth/2fa/setup');
            setQrCode(response.data.qrCode);
            setSecret(response.data.secret);
            setStep(1);
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to setup 2FA');
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const handleCopySecret = () => {
        navigator.clipboard.writeText(secret);
        setCopied(true);
        toast.success('Secret copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
    };

    const handleCodeChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').substring(0, 6);
        setCode(value);
    };

    const handleVerify = async (e) => {
        e.preventDefault();

        if (code.length !== 6) {
            toast.error('Please enter a 6-digit code');
            return;
        }

        setLoading(true);
        try {
            await api.post('/api/auth/2fa/verify-setup', { code });
            toast.success('2FA enabled successfully!');
            onSetupComplete();
            onClose();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Invalid verification code');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-md animate-fade-in">
            <div className="card-premium max-w-lg w-full p-8 relative max-h-[90vh] overflow-y-auto animate-scale-in border border-surface-200/50 dark:border-surface-700/50">
                <button
                    onClick={onClose}
                    className="absolute top-6 right-6 p-2 rounded-xl text-surface-400 hover:text-surface-900 dark:hover:text-white hover:bg-surface-100 dark:hover:bg-surface-800 transition-all"
                >
                    <X className="h-5 w-5" />
                </button>

                <div className="text-center mb-10">
                    <div className="flex justify-center mb-6">
                        <div className="p-4 bg-brand-500/10 rounded-[2rem] animate-float">
                            <Shield className="h-10 w-10 text-brand-600 dark:text-brand-400" />
                        </div>
                    </div>
                    <h3 className="text-2xl font-bold text-surface-900 dark:text-white tracking-tight">
                        Enable Security Shield
                    </h3>
                    <p className="mt-2 text-sm text-surface-500 font-medium leading-relaxed px-4">
                        {step === 1
                            ? 'Configure your physical security layer by scanning the unique matrix code below.'
                            : 'Synchronize your device by entering the temporal verification sequence generated.'
                        }
                    </p>
                </div>

                {loading && step === 1 ? (
                    <div className="flex flex-col items-center justify-center py-16 space-y-4">
                        <LoadingSpinner size="lg" />
                        <p className="text-[10px] font-bold text-surface-400 uppercase tracking-widest animate-pulse">Generating Secure Key...</p>
                    </div>
                ) : (
                    <>
                        {step === 1 && (
                            <div className="space-y-8">
                                {/* QR Code Container */}
                                <div className="relative group">
                                    <div className="absolute -inset-1 bg-gradient-to-r from-brand-500 to-purple-600 rounded-[2rem] blur opacity-25 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>
                                    <div className="relative flex justify-center p-8 bg-white rounded-[2rem] border border-surface-100/50 shadow-premium-sm overflow-hidden">
                                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(139,92,246,0.1),transparent)] pointer-events-none" />
                                        {qrCode ? (
                                            <img src={qrCode} alt="2FA QR Code" className="w-56 h-56 relative z-10" />
                                        ) : (
                                            <QrCode className="h-56 w-56 text-surface-200 animate-pulse relative z-10" />
                                        )}
                                    </div>
                                </div>

                                {/* Manual Entry Section */}
                                <div className="p-6 rounded-3xl bg-surface-50 dark:bg-surface-800/40 border border-surface-100 dark:border-surface-700/50 space-y-4">
                                    <div className="flex items-center space-x-2">
                                        <h4 className="text-xs font-bold text-surface-500 uppercase tracking-widest">
                                            Manual Entry Key
                                        </h4>
                                        <div className="h-px flex-1 bg-surface-200 dark:bg-surface-700/50"></div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <code className="flex-1 px-4 py-3 bg-white dark:bg-surface-900 rounded-2xl text-xs font-mono font-bold text-brand-600 dark:text-brand-400 border border-surface-200 dark:border-surface-700/50 break-all">
                                            {secret}
                                        </code>
                                        <button
                                            onClick={handleCopySecret}
                                            className="p-3 bg-brand-500/10 hover:bg-brand-500 text-brand-600 dark:text-brand-400 hover:text-white rounded-2xl transition-all shadow-premium-sm"
                                            title="Copy to clipboard"
                                        >
                                            {copied ? (
                                                <Check className="h-5 w-5" />
                                            ) : (
                                                <Copy className="h-5 w-5" />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Step Instructions */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="p-4 rounded-2xl bg-surface-100/50 dark:bg-surface-800/30 border border-surface-200 dark:border-surface-700/50">
                                        <span className="text-[10px] font-bold text-brand-600 uppercase">Step 01</span>
                                        <p className="text-[11px] font-medium text-surface-500 mt-1">Open your authenticator app (Google, Authy, etc)</p>
                                    </div>
                                    <div className="p-4 rounded-2xl bg-surface-100/50 dark:bg-surface-800/30 border border-surface-200 dark:border-surface-700/50">
                                        <span className="text-[10px] font-bold text-brand-600 uppercase">Step 02</span>
                                        <p className="text-[11px] font-medium text-surface-500 mt-1">Select "Add Account" and click "Scan QR Code"</p>
                                    </div>
                                </div>

                                <button
                                    onClick={() => setStep(2)}
                                    className="w-full btn-primary !py-4 font-bold tracking-wide shadow-glow-brand"
                                >
                                    Proceed to Verification
                                </button>
                            </div>
                        )}

                        {step === 2 && (
                            <form onSubmit={handleVerify} className="space-y-10 animate-fade-in">
                                <div className="space-y-6">
                                    <div className="text-center">
                                        <label className="text-xs font-bold text-surface-500 uppercase tracking-[0.3em] mb-4 block">
                                            Verification Sequence
                                        </label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            value={code}
                                            onChange={handleCodeChange}
                                            placeholder="••••••"
                                            className="w-full bg-surface-50 dark:bg-surface-800/50 border-2 border-surface-200 dark:border-surface-700/50 rounded-3xl !py-6 text-center text-4xl tracking-[0.5em] font-bold text-brand-600 dark:text-brand-400 focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 transition-all outline-none"
                                            autoFocus
                                            disabled={loading}
                                        />
                                    </div>

                                    <div className="p-6 bg-warning/5 border border-warning/20 rounded-[2rem]">
                                        <div className="flex items-start">
                                            <div className="p-2 bg-warning/10 rounded-xl mr-4 flex-shrink-0">
                                                <Shield className="h-4 w-4 text-warning" />
                                            </div>
                                            <div className="space-y-1">
                                                <p className="text-xs font-bold text-warning uppercase tracking-widest">
                                                    Production Warning
                                                </p>
                                                <p className="text-[11px] leading-relaxed font-medium text-warning/80">
                                                    Ensure you have stored your secret key in a physical vault. If you lose access to your sync device, recovery involves a manual identity audit.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setStep(1)}
                                        className="flex-1 btn-secondary !py-4 font-bold"
                                        disabled={loading}
                                    >
                                        Configuration Page
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 btn-primary !py-4 font-bold tracking-wide shadow-glow-brand"
                                        disabled={loading || code.length !== 6}
                                    >
                                        {loading ? (
                                            <LoadingSpinner size="sm" />
                                        ) : (
                                            'Seal Security Shield'
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default TwoFactorSetup;
